/**
 * CleansingPatternDashboard.tsx
 *
 * Changes from previous version
 * ───────────────────────────────
 * 1. deriveKind() and ENGINEER_KINDS moved to ./cleansingKind.ts (shared).
 *    DMTreeGridSummary's field filter now imports the same function so counts
 *    and filter results always agree.
 *
 * 2. chain_transforms and multi_map_replace show inner step breakdown in the
 *    legend when active: "8 steps: 6 Custom regex, 2 Cleanup recipe".
 *
 * 3. apply_if shows condition label + inner content summary in the legend.
 *
 * 4. KIND_META and TOKEN_TYPE_META exported so DMTreeGridSummary can use
 *    them for badge rendering without duplicating the palette.
 */

import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLayerGroup, faTags, faShuffle,
  faShieldHalved, faUserCheck, faRobot, faFileLines,
  faChevronRight, faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { T } from '../theme/theme';
import { deriveKind, ENGINEER_KINDS } from './cleansingKind';

// ─── Types ────────────────────────────────────────────────────────────────────
export type CleansingFilters = {
  transformKind: string;
  tokenType:     string;
  editability:   '' | 'editable' | 'engineer';
};

export const EMPTY_CLEANSING_FILTERS: CleansingFilters = {
  transformKind: '',
  tokenType:     '',
  editability:   '',
};

interface Props {
  tokenMap:       any | null;
  filters:        CleansingFilters;
  allowedRuleIds?: Set<string>;
  onFilterChange: (patch: Partial<CleansingFilters>) => void;
  onOpenRule?:    (sourcePrefix: string, column: string, ruleId: string) => void;
}

// ─── Kind palette (exported so DMTreeGridSummary can use it) ──────────────────
export const KIND_META: Record<string, { label: string; color: string; bg: string }> = {
  map_values:         { label: 'Value mapping',         color: T.brand,     bg: T.brandLight },
  synonym_group:      { label: 'Synonym group',          color: T.purple,    bg: T.purpleBg },
  preset:             { label: 'Cleanup recipe',         color: T.green,     bg: T.greenBg },
  reformat:           { label: 'Reformat',               color: T.amber,     bg: T.amberBg },
  regex_raw:          { label: 'Custom regex',           color: T.red,       bg: T.redBg },
  regex_replace:      { label: 'Regex replace',          color: T.red,       bg: T.redBg },
  parse_date:         { label: 'Date parsing',           color: T.teal,      bg: T.tealBg },
  normalize_case:     { label: 'Case normalization',     color: T.brand,     bg: T.brandLight },
  trim:               { label: 'Trim',                   color: T.textMuted, bg: T.bgSidebar },
  remove_whitespace:  { label: 'Strip whitespace',       color: T.textMuted, bg: T.bgSidebar },
  pad_left:           { label: 'Pad left',               color: T.textMuted, bg: T.bgSidebar },
  pad_right:          { label: 'Pad right',              color: T.textMuted, bg: T.bgSidebar },
  substring:          { label: 'Substring',              color: T.textMuted, bg: T.bgSidebar },
  round:              { label: 'Rounding',               color: T.teal,      bg: T.tealBg },
  chain_transforms:   { label: 'Step pipeline',          color: T.purple,    bg: T.purpleBg },
  multi_map_replace:  { label: 'Multi-pattern regex',    color: T.amber,     bg: T.amberBg },
  apply_if:           { label: 'Conditional transform',  color: T.teal,      bg: T.tealBg },
  unknown:            { label: 'Unknown',                color: T.textMuted, bg: T.bgSidebar },
};

// ─── Token type palette (exported) ───────────────────────────────────────────
export const TOKEN_TYPE_META: Record<string, { label: string; color: string; bg: string }> = {
  legal_suffix:    { label: 'Legal Suffix',     color: T.purple,    bg: T.purpleBg },
  country_code:    { label: 'Country Code',     color: T.brand,     bg: T.brandLight },
  currency_code:   { label: 'Currency',         color: T.green,     bg: T.greenBg },
  payment_terms:   { label: 'Payment Terms',    color: T.amber,     bg: T.amberBg },
  phone_number:    { label: 'Phone',            color: T.brand,     bg: T.brandLight },
  email_address:   { label: 'Email',            color: T.purple,    bg: T.purpleBg },
  postal_code:     { label: 'Postal Code',      color: T.brand,     bg: T.brandLight },
  street_address:  { label: 'Street Address',   color: T.amber,     bg: T.amberBg },
  address_type:    { label: 'Address Type',     color: T.green,     bg: T.greenBg },
  status_enum:     { label: 'Status / Enum',    color: T.purple,    bg: T.purpleBg },
  tax_id:          { label: 'Tax ID',           color: T.red,       bg: T.redBg },
  duns_number:     { label: 'DUNS Number',      color: T.red,       bg: T.redBg },
  entity_name:     { label: 'Entity Name',      color: T.brand,     bg: T.brandLight },
  supplier_name:   { label: 'Supplier Name',    color: T.brand,     bg: T.brandLight },
  unit_of_measure: { label: 'Unit of Measure',  color: T.teal,      bg: T.tealBg },
  product_category:{ label: 'Product Category', color: T.amber,     bg: T.amberBg },
  generic:         { label: 'Generic',          color: T.textMuted, bg: T.bgSidebar },
};

// ─── Inner step summary for composite kinds ───────────────────────────────────
function innerStepSummary(blk: any): string {
  const fn = (blk?.dsl_fn || '').toLowerCase();

  if (fn === 'chain_transforms' || fn === 'multi_map_replace') {
    const rp    = (blk.regex_pairs || []) as any[];
    const cs    = (blk.config?.chain_steps || []) as any[];
    const total = rp.length + cs.length;
    if (total === 0) return '';

    const kindCounts: Record<string, number> = {};
    rp.forEach((p: any) => {
      const k = p.classification?.kind === 'literal_list'
        ? 'synonym_group'
        : (p.classification?.kind || 'regex_raw');
      const lbl = KIND_META[k]?.label || k;
      kindCounts[lbl] = (kindCounts[lbl] || 0) + 1;
    });
    cs.forEach((s: any) => {
      const lbl = KIND_META[s.fn]?.label || s.fn || 'other';
      kindCounts[lbl] = (kindCounts[lbl] || 0) + 1;
    });

    const breakdown = Object.entries(kindCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([l, n]) => `${n} ${l}`)
      .join(', ');
    return `${total} steps: ${breakdown}`;
  }

  if (fn === 'apply_if') {
    const condLabel = blk.config?.condition_label || '';
    const innerRp   = (blk.regex_pairs || []).length;
    const innerMap  = Object.keys(blk.mapping || {}).length;
    const parts: string[] = [];
    if (condLabel) parts.push(`when: ${condLabel}`);
    if (innerRp)   parts.push(`${innerRp} pattern${innerRp > 1 ? 's' : ''}`);
    if (innerMap)  parts.push(`${innerMap} mappings`);
    return parts.join(' · ');
  }

  return '';
}

// ─── Stats computation ────────────────────────────────────────────────────────
type SynonymRow = {
  source_prefix:     string;
  column:            string;
  rule_id:           string;
  token_type:        string;
  variant_count:     number;
  canonical_count:   number;
  source:            string;
  preview_variants:  string[];
  preview_canonical: string;
};

type Stats = {
  byKind:           Record<string, number>;
  byKindDetail:     Record<string, string[]>; // kind → per-column detail lines
  byTokenType:      Record<string, number>;
  bySource:         Record<string, number>;
  topSynonymGroups: SynonymRow[];
  editableCount:    number;
  engineerCount:    number;
  totalStdRules:    number;
  distinctColumns:  number;
  distinctSources:  number;
};

function computeStats(tokenMap: any | null, allowedRuleIds?: Set<string>): Stats {
  const empty: Stats = {
    byKind: {}, byKindDetail: {}, byTokenType: {}, bySource: {},
    topSynonymGroups: [],
    editableCount: 0, engineerCount: 0,
    totalStdRules: 0, distinctColumns: 0, distinctSources: 0,
  };
  if (!tokenMap?.sources) return empty;

  const byKind:       Record<string, number>   = {};
  const byKindDetail: Record<string, string[]> = {};
  const byTokenType:  Record<string, number>   = {};
  const bySource:     Record<string, number>   = {};
  const synonyms:     SynonymRow[]             = [];
  const cols = new Set<string>();

  let editableCount = 0;
  let engineerCount = 0;
  let totalStdRules = 0;

  for (const prefix of Object.keys(tokenMap.sources)) {
    const colMap = tokenMap.sources[prefix] || {};
    for (const col of Object.keys(colMap)) {
      
      const rules = colMap[col] || {};
      for (const rid of Object.keys(rules)) {
        if (allowedRuleIds && !allowedRuleIds.has(rid)) continue;
        const blk = rules[rid] || {};
        totalStdRules++;
        cols.add(`${prefix}.${col}`);

        const src = blk.source || 'llm';
        bySource[src] = (bySource[src] || 0) + 1;

        const tt = blk.token_type || 'generic';
        byTokenType[tt] = (byTokenType[tt] || 0) + 1;

        // Shared deriveKind — identical to what DMTreeGridSummary uses for
        // field-level filtering so counts always match filter results.
        const kind = deriveKind(blk);
        byKind[kind] = (byKind[kind] || 0) + 1;

        const detail = innerStepSummary(blk);
        if (detail) {
          if (!byKindDetail[kind]) byKindDetail[kind] = [];
          byKindDetail[kind].push(`${col}: ${detail}`);
        }

        if (ENGINEER_KINDS.has(kind)) engineerCount++;
        else                          editableCount++;

        // Synonym groups
        if (blk.dsl_fn === 'map_values') {
          const mapping  = blk.mapping || {};
          const variants = Object.keys(mapping);
          if (variants.length >= 5) {
            const canonicals = new Set(Object.values(mapping));
            const preview    = variants.filter(k => mapping[k] !== k).slice(0, 3);
            synonyms.push({
              source_prefix: prefix, column: col, rule_id: rid,
              token_type: tt, variant_count: variants.length,
              canonical_count: canonicals.size, source: src,
              preview_variants: preview.length ? preview : variants.slice(0, 3),
              preview_canonical: canonicals.size === 1 ? String(Array.from(canonicals)[0]) : `${canonicals.size} canonicals`,
            });
          }
        }

        if (blk.dsl_fn === 'regex_replace') {
          const cls = blk.regex_pairs?.[0]?.classification;
          if ((cls?.kind === 'synonym_group' || cls?.kind === 'literal_list') && (cls.tokens?.length || 0) >= 2) {
            synonyms.push({
              source_prefix: prefix, column: col, rule_id: rid,
              token_type: tt, variant_count: cls.tokens.length,
              canonical_count: 1, source: src,
              preview_variants: cls.tokens.slice(0, 3),
              preview_canonical: String(cls.canonical || cls.replacement || ''),
            });
          }
        }

        if (blk.dsl_fn === 'chain_transforms' || blk.dsl_fn === 'multi_map_replace') {
          const rp = blk.regex_pairs || [];
          if (rp.length >= 3) {
            synonyms.push({
              source_prefix: prefix, column: col, rule_id: rid,
              token_type: tt, variant_count: rp.length, canonical_count: 1, source: src,
              preview_variants: rp.slice(0, 3).map((p: any) => p.pattern || ''),
              preview_canonical: `${rp.length} pattern${rp.length > 1 ? 's' : ''} → canonical`,
            });
          }
        }
      }
    }
  }

  synonyms.sort((a, b) => b.variant_count - a.variant_count);

  return {
    byKind, byKindDetail, byTokenType, bySource,
    topSynonymGroups: synonyms.slice(0, 6),
    editableCount, engineerCount,
    totalStdRules,
    distinctColumns: cols.size,
    distinctSources: Object.keys(tokenMap.sources).length,
  };
}

// ─── Active-filter chips ──────────────────────────────────────────────────────
function ActiveFilterChips({ filters, onClear }: { filters: CleansingFilters; onClear: (key: keyof CleansingFilters) => void }) {
  const chips: Array<{ key: keyof CleansingFilters; label: string; color: string; bg: string }> = [];
  if (filters.transformKind) { const m = KIND_META[filters.transformKind]; chips.push({ key: 'transformKind', label: `Kind: ${m?.label || filters.transformKind}`, color: m?.color || T.brand, bg: m?.bg || T.brandLight }); }
  if (filters.tokenType)     { const m = TOKEN_TYPE_META[filters.tokenType]; chips.push({ key: 'tokenType', label: `Token: ${m?.label || filters.tokenType}`, color: m?.color || T.brand, bg: m?.bg || T.brandLight }); }
  if (filters.editability)   { chips.push({ key: 'editability', label: filters.editability === 'editable' ? 'SME-editable only' : 'Engineer-required only', color: filters.editability === 'editable' ? T.green : T.red, bg: filters.editability === 'editable' ? T.greenBg : T.redBg }); }
  if (!chips.length) return null;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: T.textMuted, alignSelf: 'center' }}>Active:</span>
      {chips.map(c => (
        <button key={c.key} onClick={() => onClear(c.key)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 12, background: c.bg, color: c.color, border: `1px solid ${c.color}40`, cursor: 'pointer' }} title="Clear this filter">
          {c.label}<FontAwesomeIcon icon={faXmark} style={{ fontSize: 9 }} />
        </button>
      ))}
    </div>
  );
}

// ─── Transformation types panel ───────────────────────────────────────────────
function TransformKindPanel({ byKind, byKindDetail, active, onClick }: {
  byKind: Record<string, number>; byKindDetail: Record<string, string[]>;
  active: string; onClick: (k: string) => void;
}) {
  const entries = Object.entries(byKind).sort((a, b) => b[1] - a[1]);
  const total   = entries.reduce((s, [, n]) => s + n, 0);
  if (total === 0) return <PanelShell title="Transformation Types" icon={faShuffle}><Empty msg="No transformations yet." /></PanelShell>;

  return (
    <PanelShell title="Transformation Types" icon={faShuffle}
      subtitle={`${total} rule${total !== 1 ? 's' : ''} across all columns`}>

      <div style={{ display: 'flex', height: 22, borderRadius: 6, overflow: 'hidden', border: `1px solid ${T.border}`, marginBottom: 10 }}>
        {entries.map(([kind, n]) => {
          const meta = KIND_META[kind] || KIND_META.unknown;
          const pct  = (n / total) * 100;
          const dim  = active && active !== kind;
          const details = (byKindDetail[kind] || []).slice(0, 5).join('\n');
          return (
            <button key={kind} onClick={() => onClick(kind)}
              title={`${meta.label}: ${n} rule${n > 1 ? 's' : ''}${details ? '\n' + details : ''}`}
              style={{ width: `${pct}%`, height: '100%', background: meta.color, opacity: dim ? 0.3 : 1, border: 'none', cursor: 'pointer', padding: 0, transition: 'opacity .2s' }}
            />
          );
        })}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 10px' }}>
        {entries.map(([kind, n]) => {
          const meta    = KIND_META[kind] || KIND_META.unknown;
          const pct     = (n / total) * 100;
          const isActive = active === kind;
          const details  = byKindDetail[kind] || [];
          return (
            <button key={kind} onClick={() => onClick(kind)}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, padding: '5px 6px', borderRadius: 5, background: isActive ? meta.bg : 'transparent', border: isActive ? `1px solid ${meta.color}50` : '1px solid transparent', cursor: 'pointer', textAlign: 'left' as const, transition: 'background .15s' }}
              onMouseEnter={e => { if (!isActive) (e.currentTarget.style.background = T.bgSidebar); }}
              onMouseLeave={e => { if (!isActive) (e.currentTarget.style.background = 'transparent'); }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: meta.color, flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: T.textPrimary, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{meta.label}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: meta.color }}>{n}</span>
                <span style={{ fontSize: 10, color: T.textMuted, minWidth: 28, textAlign: 'right' as const }}>{pct.toFixed(0)}%</span>
              </div>
              {isActive && details.length > 0 && (
                <div style={{ fontSize: 9, color: T.textMuted, paddingLeft: 18, width: '100%' }}>
                  {details.slice(0, 3).map((d, i) => <div key={i} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d}</div>)}
                  {details.length > 3 && <div>+{details.length - 3} more columns</div>}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </PanelShell>
  );
}

// ─── Token type panel ─────────────────────────────────────────────────────────
function TokenTypePanel({ byTokenType, active, onClick }: { byTokenType: Record<string, number>; active: string; onClick: (t: string) => void }) {
  const entries = Object.entries(byTokenType).sort((a, b) => b[1] - a[1]);
  const max     = Math.max(...entries.map(e => e[1]), 1);
  if (!entries.length) return <PanelShell title="Token Type Coverage" icon={faTags}><Empty msg="No token types tagged yet." /></PanelShell>;

  return (
    <PanelShell title="Token Type Coverage" icon={faTags}
      subtitle={`${entries.length} canonical concept${entries.length !== 1 ? 's' : ''} standardized`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 220, overflowY: 'auto' }}>
        {entries.map(([tt, n]) => {
          const meta    = TOKEN_TYPE_META[tt] || TOKEN_TYPE_META.generic;
          const pct     = (n / max) * 100;
          const isActive = active === tt;
          const dim     = active && !isActive;
          return (
            <button key={tt} onClick={() => onClick(tt)}
              style={{ display: 'grid', gridTemplateColumns: '110px 1fr 26px', gap: 8, alignItems: 'center', padding: '3px 6px', borderRadius: 5, background: isActive ? meta.bg : 'transparent', border: isActive ? `1px solid ${meta.color}50` : '1px solid transparent', cursor: 'pointer', opacity: dim ? 0.4 : 1, transition: 'opacity .15s, background .15s' }}
            >
              <span style={{ fontSize: 11, fontWeight: 600, color: T.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' as const }}>{meta.label}</span>
              <div style={{ height: 12, background: T.bgSidebar, borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: meta.color, borderRadius: 3, transition: 'width .3s' }} />
              </div>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: meta.color, textAlign: 'right' as const }}>{n}</span>
            </button>
          );
        })}
      </div>
    </PanelShell>
  );
}

// ─── Shared atoms ─────────────────────────────────────────────────────────────
function PanelShell({ title, icon, subtitle, children }: { title: string; icon: any; subtitle?: string; children: React.ReactNode }) {
  return (
    <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: subtitle ? 2 : 10 }}>
        <FontAwesomeIcon icon={icon} style={{ fontSize: 11, color: T.brandMid }} />
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase' as const, color: T.textMuted }}>{title}</span>
      </div>
      {subtitle && <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 10 }}>{subtitle}</div>}
      {children}
    </div>
  );
}
function Empty({ msg }: { msg: string }) {
  return <div style={{ fontSize: 11, color: T.textMuted, padding: '8px 0' }}>{msg}</div>;
}

// ─── Main component ───────────────────────────────────────────────────────────
const CleansingPatternDashboard: React.FC<Props> = ({ tokenMap, filters, onFilterChange, allowedRuleIds }) => {
  const stats = useMemo(() => computeStats(tokenMap, allowedRuleIds), [tokenMap, allowedRuleIds]);
  if (stats.totalStdRules === 0) return null;
  const onClearFilter = (key: keyof CleansingFilters) => onFilterChange({ [key]: '' as any });
  return (
    <>
      <ActiveFilterChips filters={filters} onClear={onClearFilter} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <TransformKindPanel byKind={stats.byKind} byKindDetail={stats.byKindDetail} active={filters.transformKind} onClick={k => onFilterChange({ transformKind: filters.transformKind === k ? '' : k })} />
        <TokenTypePanel byTokenType={stats.byTokenType} active={filters.tokenType} onClick={t => onFilterChange({ tokenType: filters.tokenType === t ? '' : t })} />
      </div>
    </>
  );
};
export default CleansingPatternDashboard;

// ─── Summary chips ────────────────────────────────────────────────────────────
export function CleansingDashboardSummary(
     { tokenMap, filters, allowedRuleIds }:
     { tokenMap: any | null; filters: CleansingFilters; allowedRuleIds?: Set<string> }
  ) {
  const stats = useMemo(() => computeStats(tokenMap, allowedRuleIds), [tokenMap, allowedRuleIds]);
  if (stats.totalStdRules === 0) return null;
  const activeCount = (filters.transformKind ? 1 : 0) + (filters.tokenType ? 1 : 0) + (filters.editability ? 1 : 0);
  return (
    <>
      <span><strong style={{ color: T.brand }}>{stats.totalStdRules}</strong> std rules</span>
      <span style={{ color: T.border }}>•</span>
      <span><strong>{stats.distinctColumns}</strong> columns</span>
      <span style={{ color: T.border }}>•</span>
      <span>{Object.keys(stats.byKind).length} patterns</span>
      {activeCount > 0 && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 8, background: T.brandLight, color: T.brand, marginLeft: 'auto' }}>{activeCount} FILTER{activeCount > 1 ? 'S' : ''} ACTIVE</span>}
    </>
  );
}